// Thi hành việc dời workspace Avatar từ thư mục con lên thư mục đang đứng.
//
// Vì sao DỜI chứ không "init mới rồi gỡ cái cũ": dời nguyên thư mục giữ được 3 thứ mà
// init mới làm mất — `settings.local.json` (quyền user tự thêm), trạng thái tool đang
// bật, và hàng đợi telemetry chưa gửi. 7 symlink trong `.claude/` đều trỏ TƯƠNG ĐỐI
// (`pack/skills`…) nên chuyển cả thư mục vẫn đúng; `avatar.json`/`repos.json`/
// `avatar-tools.json`/`state/*.json` không chứa đường dẫn tuyệt đối; hook trong
// `settings.json` viết bằng `${CLAUDE_PROJECT_DIR}` nên tự trỏ đúng theo thư mục mở.
//
// ⛔ Hàm này THROW khi bất kỳ vế nào hỏng, và điều đó là CỐ Ý: `applyFixes` in
// "Fixed: ..." vô điều kiện khi `fix()` resolve (doctor.ts) — đúng lỗi đã phải vá ở
// MR !13 (`assertDependentCliFixed`). Nuốt lỗi ở đây = doctor báo đã chữa xong trong
// khi workspace vẫn nằm sai chỗ.
import { promises as fs } from "node:fs";
import { join } from "node:path";
import { createUninstallBackupSnapshot } from "./create-uninstall-backup-snapshot.js";
import { detectAvatarProjectArtifacts } from "./detect-avatar-project-artifacts.js";
import { copyDirRecursive, pathExists, removeRecursive } from "./filesystem-helpers.js";
import { readCliVersion } from "./read-cli-version-from-package-json.js";
import {
  DEFAULT_REGISTRY_PATH,
  pruneMissingWorkspaces,
  recordWorkspaceInRegistry,
} from "./workspace-registry-store.js";

export interface RelocateNestedWorkspaceResult {
  /** Thư mục backup đã tạo trước khi động vào bất cứ thứ gì. */
  backupDir: string;
  /** Entry đã dời, theo thứ tự thực hiện. */
  moved: string[];
  /** Path workspace con sau khi dời — vỏ rỗng, để người dùng tự xoá. */
  emptiedShell: string;
}

/**
 * `fs.rename` không đi qua được ranh giới thiết bị (EXDEV — thư mục con nằm trên volume
 * khác). Lùi về copy-rồi-xoá để ca đó vẫn dời được thay vì chết giữa chừng.
 */
async function moveEntry(from: string, to: string): Promise<void> {
  try {
    await fs.rename(from, to);
    return;
  } catch (err) {
    if ((err as NodeJS.ErrnoException)?.code !== "EXDEV") throw err;
  }
  const stat = await fs.lstat(from);
  // `copyDirRecursive` giữ NGUYÊN symlink (readlink + symlink, không dereference) nên
  // farm 7 symlink trong `.claude/` sang được nhánh này mà không bị phình thành bản sao.
  if (stat.isDirectory()) await copyDirRecursive(from, to);
  else await fs.copyFile(from, to);
  try {
    await removeRecursive(from);
  } catch (err) {
    // Copy XONG rồi mới hỏng lúc xoá nguồn ⇒ đích ĐÃ có bản sao đầy đủ. Không nói ra
    // thì người đi dọn tay tưởng đích còn trống và sẽ ngạc nhiên ở lần chạy sau (lúc đó
    // `cwd-path-collision` chặn, đúng nhưng khó hiểu).
    throw new Error(
      `đã sao chép sang '${to}' nhưng KHÔNG xoá được nguồn '${from}': ${err instanceof Error ? err.message : String(err)}. Đích đang có bản sao đầy đủ — tự xoá một trong hai bản trước khi chạy lại.`,
    );
  }
}

/**
 * Thứ tự dời, KHÁC thứ tự liệt kê: `.claude` đi CUỐI CÙNG.
 *
 * ⛔ Đây là bất biến an toàn, không phải sở thích. Marker nhận diện workspace là
 * `.claude/avatar.json` (`workspace-marker-constants.ts`), và `detectNestedAvatarWorkspaces`
 * TRẢ RỖNG ngay khi cwd có marker. Dời `.claude` trước rồi hỏng ở entry sau ⇒ cwd đã có
 * marker, thư mục con thì hết ⇒ lượt `doctor` KẾ TIẾP không còn nhìn thấy workspace con
 * nào cả: check này báo "ok" trong khi `CLAUDE.md`/`notes`/`src` vẫn kẹt dưới đó, và check
 * `CLAUDE.md` quay về in "chạy 'avatar init'" — đúng chỉ dẫn nguy hiểm mà cả tính năng
 * này sinh ra để chặn. Để `.claude` cuối thì hỏng giữa chừng vẫn còn marker ở chỗ cũ ⇒
 * lần sau vẫn phát hiện được, và entry đã lỡ dời rơi vào blocker `cwd-path-collision`
 * có sẵn.
 */
function orderForMove(entries: string[]): string[] {
  return [...entries].sort((a, b) => Number(a === ".claude") - Number(b === ".claude"));
}

/**
 * Dời `entries` từ `childPath` lên `cwd`.
 *
 * @param entries tên entry top-level, do `planNestedWorkspaceRelocation` chốt. Hàm này
 *   KHÔNG tự quyết định dời cái gì — trộn phép đo với phép thi hành là cách chắc nhất
 *   để một hôm nào đó nó dời nhầm code của user.
 * @param registryPath tiêm được để test không ghi vào `~/.avatar` thật.
 */
export async function relocateNestedAvatarWorkspace(
  cwd: string,
  childPath: string,
  entries: string[],
  registryPath: string = DEFAULT_REGISTRY_PATH,
): Promise<RelocateNestedWorkspaceResult> {
  if (entries.length === 0) {
    throw new Error("không có entry nào để dời — đây là lỗi lập trình, không phải trạng thái máy");
  }

  // TOCTOU: planner đo đích trống ở lượt `runChecks`, `--fix` chạy sau đó. Đo LẠI ngay
  // trước khi ghi, và đo TRỌN danh sách trước khi dời cái đầu tiên — dời được 2/4 rồi
  // mới phát hiện cái thứ 3 vướng là để lại workspace vỡ đôi.
  const blocked: string[] = [];
  for (const entry of entries) {
    if (await pathExists(join(cwd, entry))) blocked.push(entry);
  }
  if (blocked.length > 0) {
    throw new Error(
      `thư mục hiện tại đã có ${blocked.join(", ")} — KHÔNG ghi đè. Dọn hoặc đổi tên rồi chạy lại 'avatar doctor --fix'`,
    );
  }

  // Backup TRƯỚC khi động vào bất cứ thứ gì. Tái dùng đúng helper của `avatar uninstall`
  // (ghi vào ~/.avatar/uninstall-backups/) thay vì tự viết — một nơi duy nhất biết cách
  // chụp `.claude/`, và người dùng chỉ phải nhớ MỘT chỗ để tìm lại đồ cũ.
  const artifacts = detectAvatarProjectArtifacts(childPath);
  const backupDir = await createUninstallBackupSnapshot(childPath, artifacts, readCliVersion());

  const moved: string[] = [];
  for (const entry of orderForMove(entries)) {
    try {
      await moveEntry(join(childPath, entry), join(cwd, entry));
      moved.push(entry);
    } catch (err) {
      // Dừng NGAY, không cố dời tiếp: mỗi entry dời thêm là một bước xa khỏi trạng thái
      // khôi phục được. Câu lỗi phải chỉ thẳng cả hai nửa đang ở đâu.
      throw new Error(
        `dời '${entry}' thất bại: ${err instanceof Error ? err.message : String(err)}. ĐÃ dời: ${moved.length > 0 ? moved.join(", ") : "(chưa cái nào)"}. Bản sao đầy đủ nằm ở ${backupDir} — khôi phục bằng tay trước khi chạy lại.`,
      );
    }
  }

  // Sổ workspace: entry CŨ giờ mất marker (`.claude/avatar.json` đã theo `.claude/` lên
  // cwd) nên `pruneMissingWorkspaces` dọn đúng nó — KHÔNG viết logic xoá riêng. Thiếu
  // bước này thì `avatar update` sẽ sync vào một thư mục đã biến mất.
  await pruneMissingWorkspaces(registryPath);
  await recordWorkspaceInRegistry(cwd, registryPath);

  return { backupDir, moved, emptiedShell: childPath };
}
